import React from 'react';
import { motion } from 'framer-motion';

type ButtonHTMLAttributes = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onDrag' | 'onDragStart' | 'onDragEnd' | 'onAnimationStart'>;

interface ButtonProps extends ButtonHTMLAttributes {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'ghost' | 'outline' | 'link';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = false,
  disabled,
  className = '',
  ...props
}) => {
  const baseClasses = `
    font-medium rounded-lg transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed
    inline-flex items-center justify-center
  `;

  const variantClasses = {
    primary: `
      bg-primary-500 text-white 
      hover:bg-primary-600 hover:shadow-lg
      focus:ring-primary-500
      active:bg-primary-700
    `,
    secondary: `
      bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200
      border border-gray-300 dark:border-gray-600
      hover:bg-gray-50 dark:hover:bg-gray-600 hover:shadow-md
      focus:ring-gray-500
    `,
    success: `
      bg-success text-white 
      hover:bg-green-600 hover:shadow-lg
      focus:ring-green-500
      active:bg-green-700
    `,
    danger: `
      bg-error text-white 
      hover:bg-red-600 hover:shadow-lg
      focus:ring-red-500
      active:bg-red-700
    `,
    ghost: `
      bg-transparent text-gray-600 dark:text-gray-300
      hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100
      focus:ring-gray-500
    `,
    outline: `
      bg-transparent text-primary-600 
      border border-primary-600
      hover:bg-primary-50 hover:text-primary-700
      focus:ring-primary-500
    `,
    link: `
      bg-transparent text-primary-600 
      hover:text-primary-700 hover:underline
      focus:ring-primary-500
      shadow-none
    `,
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  const widthClass = fullWidth ? 'w-full' : '';

  return (
    <motion.button
      whileHover={{ scale: disabled || isLoading ? 1 : 1.02 }}
      whileTap={{ scale: disabled || isLoading ? 1 : 0.98 }}
      className={`
        ${baseClasses}
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${widthClass}
        ${className}
      `}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <Spinner size={size === 'sm' ? 'sm' : 'md'} />
          <span className="ml-2">Loading...</span>
        </>
      ) : (
        children
      )}
    </motion.button>
  );
};

const Spinner: React.FC<{ size: 'sm' | 'md' }> = ({ size }) => {
  const sizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
  };

  return (
    <div
      className={`${sizes[size]} animate-spin rounded-full border-2 border-white border-t-transparent`}
    />
  );
};

export { Button };
export default Button;
